import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

export const ChromaticAberration: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const text = 'CHROMATIC';
  const letters = text.split('');

  // Spring animation for entry
  const entryProgress = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });

  // Chromatic aberration parameters
  const aberrationIntensity = interpolate(
    Math.sin(frame * 0.1),
    [-1, 1],
    [5, 15]
  );

  const rotation = interpolate(
    frame,
    [0, fps * 5],
    [0, Math.PI * 2],
    { extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0a',
        justifyContent: 'center',
        alignItems: 'center',
        perspective: '1000px',
      }}
    >
      {/* Background grid */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          transform: `rotate(${rotation * 0.5}rad)`,
          opacity: 0.5,
        }}
      />

      {/* Main text container with RGB layers */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          transform: `scale(${entryProgress}) rotateY(${rotation * 5}deg)`,
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Red channel */}
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            transform: `translate(${-aberrationIntensity}px, ${-aberrationIntensity * 0.5}px)`,
            opacity: 0.8,
          }}
        >
          {letters.map((letter, i) => {
            const letterRotation = interpolate(
              frame + i * 3,
              [0, fps * 5],
              [0, Math.PI * 2],
              { extrapolateRight: 'clamp' }
            );
            const letterScale = interpolate(
              Math.sin(letterRotation),
              [-1, 1],
              [0.95, 1.05]
            );

            return (
              <div
                key={`red-${i}`}
                style={{
                  fontSize: '100px',
                  fontWeight: 'bold',
                  fontFamily: 'Arial Black, sans-serif',
                  color: 'rgba(255, 0, 0, 0.7)',
                  textShadow: '0 0 20px rgba(255, 0, 0, 0.8)',
                  transform: `scale(${letterScale})`,
                }}
              >
                {letter}
              </div>
            );
          })}
        </div>

        {/* Green channel */}
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            opacity: 0.8,
          }}
        >
          {letters.map((letter, i) => {
            const letterRotation = interpolate(
              frame + i * 3,
              [0, fps * 5],
              [0, Math.PI * 2],
              { extrapolateRight: 'clamp' }
            );
            const letterScale = interpolate(
              Math.sin(letterRotation),
              [-1, 1],
              [0.95, 1.05]
            );

            return (
              <div
                key={`green-${i}`}
                style={{
                  fontSize: '100px',
                  fontWeight: 'bold',
                  fontFamily: 'Arial Black, sans-serif',
                  color: 'rgba(0, 255, 0, 0.7)',
                  textShadow: '0 0 20px rgba(0, 255, 0, 0.8)',
                  transform: `scale(${letterScale})`,
                }}
              >
                {letter}
              </div>
            );
          })}
        </div>

        {/* Blue channel */}
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            transform: `translate(${aberrationIntensity}px, ${aberrationIntensity * 0.5}px)`,
            opacity: 0.8,
          }}
        >
          {letters.map((letter, i) => {
            const letterRotation = interpolate(
              frame + i * 3,
              [0, fps * 5],
              [0, Math.PI * 2],
              { extrapolateRight: 'clamp' }
            );
            const letterScale = interpolate(
              Math.sin(letterRotation),
              [-1, 1],
              [0.95, 1.05]
            );

            return (
              <div
                key={`blue-${i}`}
                style={{
                  fontSize: '100px',
                  fontWeight: 'bold',
                  fontFamily: 'Arial Black, sans-serif',
                  color: 'rgba(0, 0, 255, 0.7)',
                  textShadow: '0 0 20px rgba(0, 0, 255, 0.8)',
                  transform: `scale(${letterScale})`,
                }}
              >
                {letter}
              </div>
            );
          })}
        </div>

        {/* Main text layer (white) */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            zIndex: 10,
          }}
        >
          {letters.map((letter, i) => {
            const letterRotation = interpolate(
              frame + i * 3,
              [0, fps * 5],
              [0, Math.PI * 2],
              { extrapolateRight: 'clamp' }
            );
            const letterScale = interpolate(
              Math.sin(letterRotation),
              [-1, 1],
              [0.95, 1.05]
            );

            return (
              <div
                key={`main-${i}`}
                style={{
                  fontSize: '100px',
                  fontWeight: 'bold',
                  fontFamily: 'Arial Black, sans-serif',
                  color: 'white',
                  textShadow: `
                    0 0 10px rgba(255, 255, 255, 0.8),
                    0 0 20px rgba(255, 255, 255, 0.6)
                  `,
                  transform: `scale(${letterScale})`,
                }}
              >
                {letter}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scanlines overlay */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              rgba(0, 0, 0, 0.1) 0px,
              rgba(0, 0, 0, 0.1) 2px,
              transparent 2px,
              transparent 4px
            )
          `,
          pointerEvents: 'none',
          opacity: 0.3,
        }}
      />

      {/* Decorative particles */}
      {Array.from({ length: 30 }).map((_, i) => {
        const seed = i * 137.508;
        const particleX = Math.abs(Math.sin(seed)) * width;
        const particleY = Math.abs(Math.cos(seed * 1.3)) * height;
        const particleSize = 2 + Math.abs(Math.sin(seed * 2.7)) * 3;
        const particleOpacity = interpolate(
          Math.sin(frame * 0.05 + seed),
          [-1, 1],
          [0.2, 0.8]
        );

        return (
          <div
            key={`particle-${i}`}
            style={{
              position: 'absolute',
              left: particleX,
              top: particleY,
              width: particleSize,
              height: particleSize,
              backgroundColor: `hsl(${(seed * 360) % 360}, 100%, 60%)`,
              borderRadius: '50%',
              opacity: particleOpacity,
              boxShadow: `0 0 ${particleSize * 2}px currentColor`,
            }}
          />
        );
      })}

      {/* Aberration intensity indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          color: 'rgba(255, 255, 255, 0.6)',
          fontFamily: 'monospace',
          fontSize: '14px',
        }}
      >
        <div>RGB Shift: {aberrationIntensity.toFixed(1)}px</div>
        <div>Frame: {frame}</div>
      </div>
    </AbsoluteFill>
  );
};
